import { Buffer } from "buffer";
import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  useFragment,
  useLazyLoadQuery,
  useRelayEnvironment,
} from "react-relay";
import { graphql, requestSubscription } from "relay-runtime";
import {
  Account,
  Asset,
  bigintMin,
  MsgExecute,
  MsgMulti,
  MsgSecureWithdraw,
  MsgSend,
  MsgSwap,
} from "rujira.js";
import {
  Decimal,
  Fiat,
  i18n,
  SwapSelect,
  TokenSelect,
  useGlobalModalContext,
} from "rujira.ui";
import { AnglesRight, Info } from "rujira.ui/src/components/icons/Icons";
import {
  BalanceCompact,
  BalanceProvider,
  usePreloadedBalances,
} from "../common/components/Balance";
import {
  collectDestinationOptions,
  Destination,
  DestinationOption,
  DestinationSelectModal,
} from "../common/components/Destination";
import {
  MsgProvider,
  TxBalanceAccountButton,
} from "../common/components/TxButton";
import { useOraclePrice } from "../common/OraclePrice";
import { usePreloadedAccountData } from "../services/accountData";
import { useAccounts } from "../services/accounts";
import { QuoteRequest, useQuote } from "../services/useQuote";
import { ChunkSlider } from "../trade/components/InputQuantity";
import { BorrowAccountFragment$key } from "./__generated__/BorrowAccountFragment.graphql";
import {
  BorrowQuery,
  BorrowQuery$data,
} from "./__generated__/BorrowQuery.graphql";
import { LiquidationPrice, LtvLabel } from "./components/Health";
import { PositionRow } from "./components/Position";
import { CdpSingle } from "./components/utils";
const query = graphql`
  query BorrowQuery {
    ghostCredit {
      address
      adjustmentThreshold
      collaterals {
        asset {
          asset
          type
          chain
          metadata {
            symbol
            decimals
          }
          variants {
            native {
              denom
            }
          }
        }
        price {
          ...OraclePriceFragment
        }
        ratio
      }
      vaults {
        price {
          ...OraclePriceFragment
        }
        borrower {
          address
          asset {
            asset
            type
            chain
            metadata {
              symbol
              decimals
            }

            variants {
              secured {
                asset
                type
                chain
                metadata {
                  symbol
                  decimals
                }
              }
              native {
                denom
              }
            }
          }
          available
          current
          limit
          shares
          vault {
            interest {
              baseRate
              step1
              step2
              targetUtilization
            }
            status {
              debtRate
              depositPool {
                size
              }
              debtPool {
                size
              }
            }
          }
        }
      }
    }
    thorchainV2 {
      pools {
        status
        asset {
          asset
          type
          chain
          metadata {
            symbol
            decimals
          }

          variants {
            secured {
              asset
              type
              chain
              metadata {
                symbol
                decimals
              }
              variants {
                native {
                  denom
                }
              }
            }
          }
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment BorrowAccountFragment on Account {
    credit {
      next {
        salt
        account
      }
      accountsV2(first: 100) @connection(key: "Borrow_accountsV2") {
        __id
        edges {
          node {
            account {
              address
            }
            ...PositionBorrowRowFragment
          }
        }
      }
    }
  }
`;

const Meta = () => {
  return (
    <Helmet>
      <title>Rujira Borrow</title>
      <meta
        name="description"
        content="Borrow assets using your tokens as collateral"
      />
      <meta
        name="og:description"
        content="Borrow assets using your tokens as collateral"
      />
      <meta
        name="twitter:description"
        content="Borrow assets using your tokens as collateral"
      />
    </Helmet>
  );
};

type Collateral = NonNullable<
  BorrowQuery$data["ghostCredit"]
>["collaterals"][number];

type Vault = NonNullable<BorrowQuery$data["ghostCredit"]>["vaults"][number];

export const Borrow = () => {
  const data = useLazyLoadQuery<BorrowQuery>(query, {});
  const collaterals = data.ghostCredit?.collaterals || [];
  const [collateral, setCollateral] = useState<Collateral>(
    collaterals.find((a) => a.asset.asset === "BTC-BTC") || collaterals[0]
  );
  if (!collateral)
    return (
      <div className="rujira__main rujira__main--gradient borrow">
        <div className="rujira__inner--pad">
          <div className="rujira__inner">
            <i18n.h1 className="h1">Borrow</i18n.h1>
            <i18n.h2 className="fs-24 lh-32 fw-400 color-white balance-text">
              Access liquidity to gain exposure to market movements or finance
              your expenses, without selling your holdings.
            </i18n.h2>
            <i18n.p className="text-center mt-12 fs-24 color-grey">
              Arriving Imminently...
            </i18n.p>
          </div>
        </div>
      </div>
    );
  return (
    <BalanceProvider asset={collateral.asset}>
      <Meta />
      <Content
        data={data}
        setCollateralAsset={(x) => {
          const c = collaterals.find((a) => x.asset === a.asset.asset);
          if (!c) return;
          return setCollateral(c);
        }}
        collateral={collateral}
      />
    </BalanceProvider>
  );
};

const Content: FC<{
  data: BorrowQuery$data;
  collateral: Collateral;
  setCollateralAsset: (v: Asset) => void;
}> = ({ data, collateral, setCollateralAsset }) => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<BorrowAccountFragment$key>(
    accountFragment,
    accountData
  );
  const { accounts } = useAccounts();

  const destinationOptions: DestinationOption[] =
    (data.thorchainV2?.pools || [])
      .reduce((a: Asset[], v) => {
        if (v.status !== "AVAILABLE") return a;
        if (
          !data.ghostCredit?.vaults.find(
            (x) => x.borrower.asset.metadata.symbol === v.asset.metadata.symbol
          )
        )
          return a;
        return [v.asset, ...a];
      }, [])
      .flatMap(collectDestinationOptions(accounts || []))
      .sort((a, b) => {
        return a.asset.metadata.symbol.localeCompare(b.asset.metadata.symbol);
      }) || [];

  const [collateralAmount, setCollateralAmount] = useState(0n);
  const [destination, setDestination] = useState<Destination | undefined>(
    destinationOptions.find((a) => a.asset.asset.startsWith("ETH-USDC"))
  );
  const [debtAmount, setDebtAmount] = useState(0n);
  const vault =
    data.ghostCredit?.vaults.find((a) => {
      return (
        destination &&
        a.borrower.asset.metadata.symbol === destination.asset.metadata.symbol
      );
    }) ||
    data.ghostCredit?.vaults.find((a) =>
      a.borrower.asset.asset.startsWith("ETH-USDC")
    );

  const collateralPrice = useOraclePrice(collateral.price) || 0n;
  const debtPrice = useOraclePrice(vault?.price) || 0n;

  if (!vault) return null;

  const p = CdpSingle.fromInput(
    {
      amount: collateralAmount,
      price: collateralPrice,
      asset: collateral.asset,
      ratio: collateral.ratio,
    },
    {
      amount: debtAmount,
      price: debtPrice,
      rate: vault.borrower.vault.status.debtRate,
      asset: vault.borrower.asset,
    },
    { adjustmentMax: BigInt(data.ghostCredit?.adjustmentThreshold || 0) },
    account?.credit.next || undefined
  );

  return (
    <>
      <div className="rujira__main rujira__main--gradient borrow">
        <div className="rujira__inner--pad">
          <div className="rujira__inner">
            <i18n.h1 className="h1">Borrow</i18n.h1>
            <i18n.h2 className="fs-24 lh-32 fw-400 color-white balance-text">
              Access liquidity to gain exposure to market movements or finance
              your expenses, without selling your holdings.
            </i18n.h2>
            <div className="flex jc-c wrap mt-6">
              <InputCollateral
                data={data}
                collateral={collateral}
                setCollateralAsset={setCollateralAsset}
                collateralAmount={collateralAmount}
                setCollateralAmount={setCollateralAmount}
                p={p}
              />
              <div className="borrow__arrow">
                <AnglesRight />
              </div>
              <InputDebt
                data={data}
                setDestination={setDestination}
                destination={destination}
                debtAmount={debtAmount}
                setDebtAmount={setDebtAmount}
                vault={vault}
                p={p}
                options={destinationOptions}
              />
            </div>

            <Submit
              data={data}
              collateralAmount={collateralAmount}
              collateralAsset={collateral.asset}
              debtAmount={debtAmount}
              debtAsset={vault?.borrower.asset}
              destination={destination}
            />

            <Positions data={data} />
          </div>
        </div>
      </div>
    </>
  );
};

const InputCollateral: FC<{
  data: BorrowQuery$data;
  collateral: Collateral;
  setCollateralAsset: (v: Asset) => void;
  collateralAmount: bigint;
  setCollateralAmount: (v: bigint) => void;
  p: CdpSingle<Asset>;
}> = ({
  data,
  collateral,
  setCollateralAsset,
  collateralAmount,
  setCollateralAmount,
  p,
}) => {
  const vaults = data.ghostCredit?.vaults || [];
  const collaterals = data.ghostCredit?.collaterals || [];

  if (!vaults.length && !collaterals.length)
    throw new Error(`Borrow not available yet on ${import.meta.env.MODE}net`);

  const balances = usePreloadedBalances();
  const collateralOptions = collaterals.map((v) => ({
    asset: v.asset,
    balance:
      balances?.find((a) => a.asset.asset === v.asset.asset)?.balance || 0n,
  }));

  const collateralPrice = useOraclePrice(collateral.price) || 0n;

  return (
    <div className="swap__select">
      <i18n.h3 className="h4 mb-1">Deposit</i18n.h3>
      <BalanceCompact
        className="mb-0.5 px-1"
        onClick={(v) => setCollateralAmount(v.balance)}
      />
      <SwapSelect
        selected={collateral.asset}
        options={collateralOptions}
        onSelect={(v) => {
          setCollateralAsset(v);
        }}
        amount={collateralAmount}
        onChangeAmount={(a: bigint) => setCollateralAmount(a)}
        className="swap-select--responsive"
        value={p.collateral.value}
      />
      <div className="row wrap pad-mini px-0.5 px-sm-2.5 mt-1.5 condensed fs-14">
        <div className="col-6 text-left color-grey fw-500 py-0.5">
          {collateral.asset.metadata.symbol} {i18n.t("Price")}
        </div>
        <div className="col-6 text-right py-0.5">
          <Fiat amount={collateralPrice} decimals={12} symbol="$" />
        </div>
        <div className="col-6 text-left color-grey fw-500 py-0.5">
          <div
            data-tooltip-id="global-tip"
            data-tooltip-html={`<div class='w-20'>Multiplier applied to the asset's oracle price to determine its value as collateral for debt positions</div>`}
            className="iflex ai-c no-select">
            {i18n.t("Collateral Ratio")}
            <Info className="ml-0.5 block w-2" />
          </div>
        </div>
        <div className="col-6 text-right py-0.5">
          {(Number(collateral.ratio) / 1e12).toLocaleDecimal(2)}
        </div>

        <div className="col-6 text-left color-grey fw-500 py-0.5">
          <div
            data-tooltip-id="global-tip"
            data-tooltip-html={`The value of ${collateral.asset.metadata.symbol} collateral when calculating loan LTV`}
            className="iflex ai-c no-select">
            {i18n.t("Adjusted Collateral Value")}
            <Info className="ml-0.5 block w-2" />
          </div>
        </div>
        <div className="col-6 text-right py-0.5">
          <Fiat amount={p.collateral.valueAdjusted} decimals={8} symbol="$" />
        </div>
      </div>
    </div>
  );
};

const InputDebt: FC<{
  data: BorrowQuery$data;
  destination?: Destination;
  setDestination: (v: Destination) => void;
  debtAmount: bigint;
  setDebtAmount: (v: bigint) => void;
  p: CdpSingle<Asset>;
  vault: Vault;
  options: DestinationOption[];
}> = ({
  data,
  debtAmount,
  setDebtAmount,
  destination,
  setDestination,
  p,
  vault,
  options,
}) => {
  const { showModal, hideModal } = useGlobalModalContext();
  const [to, setTo] = useState(p.debt.asset.metadata.symbol);
  const openModal = () => {
    showModal({
      backgroundClose: true,
      className: "modal--xl modal--fixed modal--nopad",
      children: (
        <DestinationSelectModal
          setTo={setTo}
          to={to}
          destination={destination}
          setDestination={(v) => {
            setDestination(v);
            hideModal();
          }}
          options={options}
        />
      ),
    });
  };

  const debtDepositSize = vault.borrower.vault.status.depositPool.size;
  const debtDebtSize = vault.borrower.vault.status.debtPool.size;
  const debtAvailable = bigintMin(
    debtDepositSize - debtDebtSize,
    vault.borrower.available
  );
  const debtMax = bigintMin(p.debt.maxAdjust, debtAvailable);

  return (
    <div className="swap__select">
      <i18n.h3 className="h4 mb-1 mb-lg-3.5">Borrow</i18n.h3>
      <div className="row wrap pad--sm gap-y-1 jc-c">
        <div className="col-12 col-md-6 col-lg-6">
          <TokenSelect
            selected={destination?.asset || p.debt.asset}
            onClick={options.length ? openModal : undefined}
            network={
              destination?.asset.type === "SECURED"
                ? "THOR"
                : destination?.asset.chain
            }
            address={destination?.address}
          />
        </div>
        <div className="col-12 col-md-6 col-lg-6">
          <SwapSelect
            selected={p.debt.asset}
            amount={debtAmount}
            onChangeAmount={setDebtAmount}
            value={p.debt.value}
            hideSelected={true}
            className="swap-select--responsive"
          />
        </div>
      </div>

      <div className="flex ai-c mt-1.5 mx-0.5 mx-sm-1">
        <a
          className="fs-12 condensed fw-500 color-grey mr-0.5 pointer"
          onClick={() => setDebtAmount(0n)}>
          0%
        </a>
        <ChunkSlider
          amount={debtAmount}
          setAmount={setDebtAmount}
          max={debtMax}
        />
        <a
          className="fs-12 condensed fw-500 color-grey ml-0.5 pointer"
          onClick={() => setDebtAmount(debtMax)}>
          100%
        </a>
      </div>
      <InputDebtSummary
        data={data}
        debtAsset={p.debt.asset}
        debtAmount={debtAmount}
        debtDepositSize={debtDepositSize}
        debtDebtSize={debtDebtSize}
        debtAvailable={debtAvailable}
        p={p}
        vault={vault}
      />
    </div>
  );
};

const InputDebtSummary: FC<{
  data: BorrowQuery$data;
  debtAsset: Asset;
  debtAmount: bigint;
  p: CdpSingle<Asset>;
  debtDepositSize: bigint;
  debtDebtSize: bigint;
  debtAvailable: bigint;
  vault: Vault;
}> = ({
  data,
  debtAmount,
  debtAsset,
  p,
  debtDepositSize,
  debtDebtSize,
  debtAvailable,
  vault,
}) => {
  const vaults = data.ghostCredit?.vaults || [];
  const collaterals = data.ghostCredit?.collaterals || [];

  if (!vaults.length && !collaterals.length)
    throw new Error(`Borrow not available yet on ${import.meta.env.MODE}net`);

  const debtPrice = p.debt.price;
  // Compute borrow apr for target borrow
  const newDebtDebtSize =
    debtAmount > debtAvailable ? debtDepositSize : debtDebtSize + debtAmount;

  const baseRate = vault.borrower.vault.interest.baseRate;
  const step1 = vault.borrower.vault.interest.step1;
  const step2 = vault.borrower.vault.interest.step2;
  const targetRatio = vault.borrower.vault.interest.targetUtilization;
  const newRatio = (newDebtDebtSize * 10n ** 12n) / debtDepositSize;

  const borrowApr =
    newRatio < targetRatio
      ? baseRate + (newRatio * step1) / targetRatio
      : baseRate +
        step1 +
        ((newRatio - targetRatio) * (step2 - step1)) /
          (10n ** 12n - targetRatio);

  return (
    <div className="row wrap pad-mini px-0.5 px-sm-2.5 mt-1.5 condensed fs-14">
      <div className="col-6 text-left color-grey fw-500 py-0.5">
        {debtAsset.metadata.symbol} {i18n.t("Price")}
      </div>
      <div className="col-6 text-right py-0.5">
        <Fiat amount={debtPrice} decimals={12} symbol="$" />
      </div>
      <div className="col-6 text-left color-grey fw-500 py-0.5">
        {debtAsset.metadata.symbol} {i18n.t("Available")}
      </div>
      <div className="col-6 text-right py-0.5">
        <Decimal amount={debtAvailable} round={2} />

        <Fiat
          className="color-grey ml-1"
          symbol="$"
          decimals={20}
          amount={debtPrice * debtAvailable}
        />
      </div>

      <div className="col-6 text-left color-grey fw-500 py-0.5">
        <div
          data-tooltip-id="global-tip"
          data-tooltip-html="<div class='w-20'>Dynamic yearly interest rate, charged per-block, varies based on supply & demand</div>"
          className="iflex ai-c no-select">
          {i18n.t("Annual interest Rate")}
          <Info className="ml-0.5 block w-2" />
        </div>
      </div>
      <div className="col-6 text-right">
        <div
          style={{ marginRight: "-0.5rem" }}
          className={clsx({
            "tag tag--borderless": true,
            "tag--grey": true,
          })}>
          {(Number(borrowApr) / 1e10).toLocaleDecimal(2)}%
        </div>
      </div>

      <div className="col-6 text-left color-grey fw-500 py-0.5">
        <div
          data-tooltip-id="global-tip"
          data-tooltip-html="Ratio of the Debt Value to the Adjusted Collateral Value"
          className="iflex ai-c no-select">
          {i18n.t("Adjusted LTV")}
          <Info className="ml-0.5 block w-2" />
        </div>
      </div>
      <div className="col-6 text-right">
        <div style={{ marginRight: "-0.5rem" }}>
          <LtvLabel ltv={p.ltv} />
        </div>
      </div>

      <div className="col-6 text-left color-grey fw-500 py-0.5">
        <div
          data-tooltip-id="global-tip"
          data-tooltip-html={`The ${(["USDC", "USDT"].includes(p.collateral.asset.metadata.symbol) ? p.debt : p.collateral).asset.metadata.symbol} price at which your position can be liquidated`}
          className="iflex ai-c no-select">
          {i18n.t("Liquidation Price")}
          <Info className="ml-0.5 block w-2" />
        </div>
      </div>
      <div className="col-6 text-right  py-0.5">
        <div style={{ marginRight: "-0.5rem" }}>
          <LiquidationPrice p={p} />
        </div>
      </div>
    </div>
  );
};

const Submit: FC<{
  data: BorrowQuery$data;
  collateralAmount: bigint;
  collateralAsset: Asset;
  debtAmount: bigint;
  debtAsset: Asset;
  destination?: Destination;
}> = ({
  data,
  debtAmount,
  debtAsset,
  collateralAmount,
  collateralAsset,
  destination,
}) => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<BorrowAccountFragment$key>(
    accountFragment,
    accountData
  );

  const registry = data.ghostCredit?.address || "";
  const { selected } = useAccounts();
  const req: QuoteRequest | null = useMemo(() => {
    if (!destination) return null;
    return {
      from: debtAsset.asset,
      to: destination.asset.asset,
      amount: debtAmount,
      destination: destination.address,
      liquidityToleranceBps: 50n,
    };
  }, [debtAsset, debtAmount, destination]);

  const quote = useQuote(req);

  const msg = useMemo(() => {
    if (!(collateralAmount && debtAmount && selected)) return null;
    const borrowAmount = {
      denom: debtAsset.variants?.native?.denom,
      amount: debtAmount.toString(),
    };

    const next = account?.credit.next;
    if (!next) return null;
    if (!destination) return null;
    const acc: Account = { address: selected.address, network: "THOR" };
    const isWithdrawal = destination.asset.type == "LAYER_1";
    const base = [
      new MsgExecute(acc, [], registry, {
        create: {
          salt: next.salt,
          label: `${collateralAsset.asset}/${debtAsset.asset}`,
          tag: "single",
        },
      }),
      new MsgSend(acc, collateralAsset, collateralAmount, next.account),
      new MsgExecute(acc, [], registry, {
        account: {
          addr: next.account,
          msgs: [
            { borrow: borrowAmount },
            {
              send: {
                funds: [borrowAmount],
                to_address: selected.address,
              },
            },
          ],
        },
      }),
    ];
    if (!isWithdrawal) return new MsgMulti(acc, base);
    if (destination.asset.chain === debtAsset.chain)
      return new MsgMulti(acc, [
        ...base,
        new MsgSecureWithdraw(
          acc,
          destination.asset.variants?.secured!,
          debtAmount,
          destination.address
        ),
      ]);

    if (!(typeof quote === "object" && "memo" in quote)) return null;
    return new MsgMulti(acc, [
      ...base,
      new MsgSwap(acc, debtAsset, debtAmount, quote.memo),
    ]);
  }, [
    collateralAmount,
    debtAmount,
    selected,
    debtAsset,
    collateralAsset,
    destination,
  ]);

  return (
    <MsgProvider msg={msg}>
      <div className="mt-3 text-center">
        <TxBalanceAccountButton
          label={`Borrow ${debtAsset.metadata.symbol}`}
          required={{
            asset: collateralAsset,
            amount: collateralAmount,
          }}
        />
      </div>
    </MsgProvider>
  );
};

const subscription = graphql`
  subscription BorrowSubscription($connection: ID!, $prefix: String!) {
    edge(prefix: $prefix) @appendEdge(connections: [$connection]) {
      cursor
      node {
        ... on GhostCreditAccount {
          ...PositionBorrowRowFragment
        }
      }
    }
  }
`;

const Positions: FC<{
  data: BorrowQuery$data;
}> = ({ data }) => {
  const { selected } = useAccounts();
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<BorrowAccountFragment$key>(
    accountFragment,
    accountData
  );

  const accounts = account?.credit.accountsV2.edges?.map((a) => a?.node!);
  const env = useRelayEnvironment();

  useEffect(() => {
    if (!account?.credit.accountsV2.__id) return;
    if (!selected) return;
    const { dispose } = requestSubscription(env, {
      subscription,
      variables: {
        prefix: Buffer.from(`GhostCreditAccount:${selected.address}`).toString(
          "base64"
        ),
        connection: account.credit.accountsV2.__id,
      },
    });
    return () => {
      dispose();
    };
  }, [selected, account]);

  return (
    accounts &&
    accounts.length > 0 && (
      <>
        <hr className="hr mt-6 mb-4 opacity-10" />

        <i18n.h3 className="h4 mb-1">My Positions</i18n.h3>
        <div className="card relative mt-2 p-3">
          <div className="table-sticky">
            <table className="table table--big condensed">
              <thead>
                <tr>
                  <th>Deposited</th>
                  <th>Borrowed</th>
                  <th>Interest Rate</th>
                  <th>
                    <div
                      className="iflex ai-c no-select"
                      data-tooltip-id="global-tip"
                      data-tooltip-html="Adjusted LTV determines your risk of liquidation. At 100%, your position can be liquidated.">
                      Adjusted LTV
                      <Info className="ml-0.5 block w-2" />
                    </div>
                  </th>
                  <th>Liquidation Price</th>
                  <th className="w-1" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <PositionRow
                    k={a}
                    key={a.account.address}
                    adjustmentThreshold={
                      data.ghostCredit?.adjustmentThreshold || 0n
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  );
};
